import type React from "react";
import { useCallback } from "react";
import { useScheduledImageSrc } from "../../hooks/useScheduledImageSrc";
import { Avatar, AvatarFallback } from "@planta-v2/pds-ui/avatar";
import { PlantaAvatarImage } from "@planta-v2/pds-ui/planta-avatar-image";
import { StringUtils } from "../../utils/string.utils";
import { formatProfileSubtitle } from "@shared/utils/profile-subtitle.utils";
import { isProfileBasedSuggestion } from "@shared/schemas/profile-based-suggestion.schemas";
import { LocalizedTextUtils } from "@shared/utils/localized-text.utils";
import { EventParticipantIdPublic } from "@shared/schemas/branded-id.schemas";
import type { TopPick } from "@frontend/shared/domain/types/top-pick.types";
import { Badge } from "@planta-v2/pds-ui/badge";
import { ChevronRight } from "lucide-react";
import { i18n } from "@frontend/i18n";

interface TopPicksListItemProps {
  readonly pick: TopPick;
  readonly onClick: (participantIdPublic: EventParticipantIdPublic) => void;
}

export function TopPicksListItem({
  pick,
  onClick,
}: TopPicksListItemProps): React.JSX.Element {
  const { entry, suggestions } = pick;
  const { displayName, company, photoUrl, participantIdPublic, headline } =
    entry;
  const subtitle = formatProfileSubtitle(
    { title: headline, companyName: company },
    ({ position, company: companyName }) =>
      i18n.t("shared.profile.subtitle.withCompany", {
        position,
        company: companyName,
      }),
  );
  const initials = StringUtils.getInitials(displayName);
  // Not gated on `config.showOverrideBadge`, which this client does receive: the
  // backend already dropped the key, so presence is the whole condition.
  const isOverride = suggestions.some(
    (suggestion) => suggestion.isOverride === true,
  );
  const { ref, scheduledSrc, onLoadingStatusChange } =
    useScheduledImageSrc(photoUrl);

  // Extract first suggestion for summary display

  const firstSuggestion = suggestions[0];
  const whyConnectSummary =
    firstSuggestion !== undefined && isProfileBasedSuggestion(firstSuggestion)
      ? LocalizedTextUtils.resolve(firstSuggestion.whyConnect, i18n.getLocale())
      : null;

  const handleClick = useCallback(
    (_event: React.MouseEvent<HTMLButtonElement>): void => {
      onClick(participantIdPublic);
    },
    [onClick, participantIdPublic],
  );

  return (
    <button
      ref={ref}
      type="button"
      onClick={handleClick}
      className="flex w-full items-start gap-3 py-3 hover:bg-accent/50 transition-colors text-left border-b border-foreground/10 last:border-b-0"
      data-testid={`directory-top-pick-item-${EventParticipantIdPublic.unwrap(
        participantIdPublic,
      )}`}
    >
      <Avatar className="h-10 w-10 shrink-0 rounded-full border border-foreground/10">
        {scheduledSrc !== null ? (
          <PlantaAvatarImage
            src={scheduledSrc}
            alt={displayName}
            className="object-cover"
            onLoadingStatusChange={onLoadingStatusChange}
          />
        ) : null}
        <AvatarFallback className="bg-muted-foreground/20 font-serif text-sm font-normal text-foreground/75">
          {initials}
        </AvatarFallback>
      </Avatar>

      <div className="flex flex-col min-w-0 flex-1 gap-px">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-serif text-base font-normal text-foreground leading-tight truncate">
            {displayName}
          </span>
          {isOverride && (
            <Badge variant="outline" className="px-2 py-0 text-pds-micro">
              {i18n.t("participantProfile.topPicks.overrideBadge")}
            </Badge>
          )}
        </div>
        {subtitle !== "" && (
          <span className="text-pds-2xs text-muted-foreground leading-tight line-clamp-2">
            {subtitle}
          </span>
        )}
        {whyConnectSummary !== null && (
          <p className="mt-1.5 text-sm text-foreground/80 leading-snug line-clamp-2">
            {whyConnectSummary}
          </p>
        )}
      </div>

      <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/60" />
    </button>
  );
}
